import axios from "axios";
import { message as AntdAlert } from "antd";
import { put } from "redux-saga/effects";
import * as actions from "../actions";
import * as authActions from "../../../login/redux/actions";
import { baseURL } from "../../../../globalUtils/axiosConfig";
import { errorHandler } from "../../../../components/unauthorizeHandler";

export function* handleCreateUser({ data }) {
  try {
    // Always use FormData to handle both cases consistently
    const fd = new FormData();
    
    for (const [key, value] of Object.entries(data)) {
      if (key === "id") continue;
      
      if (key === "image" && value !== undefined) {
        // Handle image file upload
        if (value) {
          fd.append('image', value);
        }
        continue;
      }
      
      if (key === "permissions") {
        fd.append("permissions", JSON.stringify(value));
      } 
      else if (value !== null && value !== undefined) {
        fd.append(key, value);
      }
    }
    
    const response = yield axios.post(`${baseURL}/user`, fd, {
      headers: {
        "Content-Type": "multipart/form-data",
      }
    });
    
    if (response.status === 201) {
      AntdAlert.success("User Created Successfully");
      yield put(actions.createUserSuccess(response.data));
    } else {
      console.log('response.data', response.data)
      AntdAlert.error(response.data?.errors[0] || "Something went wrong!");
      yield put(actions.createUserError());
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("API Error:", error);
    const errorMessage = error.response?.data?.errors?.[0] || 
                        error.response?.data?.message || 
                        error.message || 
                        "Failed to create user. Please try again.";
    AntdAlert.error(errorMessage);
    yield put(actions.createUserError());
  }
}

export function* handleUpdateUser({ data }) {
  try {
    // Always use FormData to handle both cases consistently
    const fd = new FormData();
    
    for (const [key, value] of Object.entries(data)) {
      if (key === "id") continue;
      
      if (key === "image" && value !== undefined) {
        // Handle image file upload
        if (value) {
          fd.append('image', value);
        }
        continue;
      }
      
      if (key === "permissions") {
        fd.append("permissions", JSON.stringify(value));
      } 
      else if (value !== null && value !== undefined) {
        fd.append(key, value);
      }
    }
    
    // Log FormData entries for debugging
    for (const pair of fd.entries()) {
      if (pair[1] instanceof File) {
        console.log(`${pair[0]}: File - ${pair[1].name} (${pair[1].type}, ${pair[1].size} bytes)`);
      } else {
        console.log(`${pair[0]}: ${pair[1]}`);
      }
    }

    const response = yield axios.patch(`${baseURL}/user/${data.id}`, fd, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    if (response.status === 200) {
      AntdAlert.success("User Updated Successfully");
      // Process the response data to ensure image URL is properly set
      const userData = {
        ...response.data,
        imageUrl: response.data.attachments?.[0] ? {
          url: `${baseURL}/${response.data.attachments[0].path}`,
          name: response.data.attachments[0].originalName,
          mimetype: response.data.attachments[0].mimetype,
          size: response.data.attachments[0].size
        } : null
      };
      
      yield put(actions.updateUserSuccess(userData));
      let user = JSON.parse(localStorage.getItem("user"));
      console.log('User.data', userData)
      const { id, userType } = userData;
      if (user?.id === id && user.userType === userType) {
        yield put(authActions.setUser(userData));
        localStorage.setItem("user", JSON.stringify(userData));
      }
    } else if (response.status !== 200) {
      AntdAlert.error(response.data?.errors[0]);
      yield put(actions.updateUserError());
      errorHandler(response.data);
    }
  } catch (error) {
    console.log("API Error:", error);
    const errorMessage = error.response?.data?.errors?.[0] || 
                        error.response?.data?.message || 
                        error.message || 
                        "Failed to update user. Please try again.";
    AntdAlert.error(errorMessage);
    yield put(actions.updateUserError());
  }
}

export function* handleFetchUser({ data }) {
  try {
    const response = yield axios.get(`${baseURL}/user/${data}`);
    if (response.status === 200) {
      yield put(actions.fetchUserSuccess(response.data));
      let user = JSON.parse(localStorage.getItem("user"));
      const { id, userType } = response.data;
      if (user?.id === id && user.userType === userType) {
        yield put(authActions.setUser(response.data));
        localStorage.setItem("user", JSON.stringify(response.data));
      }
    } else {
      yield put(actions.updateUserError());
      errorHandler(response.data);
    }
  } catch (error) {
    console.log(error);
  }
}

export function* handleFetchAllUsers({ data }) {
  const { search, ...rest } = data;
  Object.keys(search).forEach((key) => {
    if (search[key]?.length < 1 || search[key] === null) delete search[key];
  });

  try {
    // Fetch users
    const response = yield axios.get(`${baseURL}/user`, {
      params: { ...rest, ...(Object.keys(search).length > 0 && { ...search }) },
    });
        
    if (response.status === 200) {
      // Process the response data
      const { users, total } = response.data;
      
      // Map department data from the nested department object
      const processedUsers = users.map(user => ({
        ...user,
        // Add departmentName for display in the table
        departmentName: user.department ? user.department.name : 'N/A'
      }));
      
      yield put(actions.fetchAllUsersSuccess({
        users: processedUsers,
        total
      }));
    }
    
    errorHandler(response.data);
  } catch (error) {
    AntdAlert.error("Something went wrong!");
    yield put(actions.fetchAllUsersError());
    console.log(error);
  }
}

export function* handleDeleteUser({ data }) {
  try {
    const response = yield axios.delete(`${baseURL}/user/${data.id}`);
    // const { success, message } = response.data;
    if (response.status === 200) {
      AntdAlert.success("User deleted successfully");
      yield put(actions.deleteUserSuccess(response.data));
    } else if (response.status !== 200) {
      errorHandler(response.data);
      yield put(actions.deleteUserError(response.data));
    }
  } catch ({ response }) {
    yield put(actions.deleteUserError(response.data));
    AntdAlert.error("Something went wrong!");
    console.log(response);
  }
}

export function* handleUpdateUserPermissions({ data }) {
  const { permissions, id } = data;
  try {
    const response = yield axios.patch(`${baseURL}/user/${id}`, {
      permissions,
    });
    console.log("handleUpdateUserPermissions", response)
    if (response.status === 200) {
      AntdAlert.success("User permissions updated successfully");
      yield put(actions.updateUserPermissionsSuccess(response.data));
      let user = JSON.parse(localStorage.getItem("user"));
      const { id, userType } = response.data;
      if (user?.id === id && user.userType === userType) {
        yield put(authActions.setUser(response.data));
        localStorage.setItem("user", JSON.stringify(response.data));
      }
    } else if (response.status !== 200) {
      AntdAlert.error(response.data?.errors[0]);
      yield put(actions.updateUserPermissionsError());
      errorHandler(response.data);
    }
  } catch (error) {
    console.log(error);
  }
}

export function* handleUpdateUserSettings({ data }) {  
  const { loadingObj } = data;

  let obj = {};
  if (loadingObj?.popupLoading) {
    obj = { popupLoading: false };
  }
  if (loadingObj?.emailLoading) {
    obj = { emailLoading: false };
  }

  try {
    const response = yield axios.patch(
      `${baseURL}/user/${data.data.id}`,
      {
        ...data.data,
      }
    );
    const { success, message } = response.data;

    if (success) {
      AntdAlert.success(message);
      yield put(
        actions.updateUserSettingsSuccess({
          data: response.data.data,
          loadingObj: obj,
        })
      );
    } else if (!success) {
      AntdAlert.error(response.data?.errors[0]);
      errorHandler(response.data);
      yield put(actions.updateUserSettingsError());
    }
  } catch (error) {
    console.log(error);
  }
}

export function* handleAddDepartment({ data }) {
  try {
    const response = yield axios.post(`${baseURL}/api/v1/departments`, {
      ...data,
    });
    const { success, message } = response.data;

    if (success) {
      AntdAlert.success(message);
      yield put(actions.saveDepartmentSuccess(response.data.data));
      yield put(actions.setDepartDisableIndex(data.departments.length - 1));
    } else if (!success) {
      errorHandler(response.data);
      AntdAlert.error(response.data?.errors[0]);
      yield put(actions.saveDepartmentError());
    }
  } catch (error) {
    console.log(error);
  }
}

export function* handleSendDetailsToUser({ data }) {
  try {
    const response = yield axios.post(
      `${baseURL}/user/send-login-details/${data}`
    );
    const { success, message } = response.data;
    if (success) {
      AntdAlert.success(message);
      yield put(actions.sendDetailsToUserSuccess());
    } else if (!success) {
      AntdAlert.error(message);
      errorHandler(response.data);
      yield put(actions.sendDetailsToUserError());
    }
  } catch ({ response }) {
    const { status } = response;
    if (status == 500 || status == 404) {
      AntdAlert.error("Something went wrong!");
      yield put(actions.sendDetailsToUserError());
    }
  }
}

export function* handleCreateUserProject({ data }) {
  try {
    const response = yield axios.post(`${baseURL}/user_projects`, {
      ...data,
    });
    
    if (response.status >= 200 && response.status < 300) {
      const responseData = response.data || {};
      AntdAlert.success("Project permissions created successfully");
      yield put(actions.createUserProjectSuccess(responseData));
      yield put(actions.fetchAllUserProjects({
        userId: data.userId,
        page: 1,
        resultPerPage: 10
      }));
      yield put(actions.fetchAllUserProjects({
        userId: data.userId,
        page: 1,
        resultPerPage: 10
      }));
      
      if (responseData.userId || data.userId) {
        const userId = responseData.userId || data.userId;
        yield put(authActions.fetchSidebarProject({ userId }));
      }
    } else {
      const errorMsg = response.data?.errors && Array.isArray(response.data.errors) && response.data.errors.length > 0
        ? response.data.errors[0]
        : response.data?.message || "Something went wrong with the request!";
      AntdAlert.error(errorMsg);
      yield put(actions.createUserProjectError());
      errorHandler(response);
    }
  } catch (error) {
    if (error.message && error.message.includes('Network Error')) {
      AntdAlert.warning("Network issue detected. The permission may have been created but couldn't be confirmed. Please refresh to check.");
    } else {
      const errorMessage = 
        (error.response?.data?.errors && Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0)
          ? error.response.data.errors[0]
          : error.response?.data?.message || error.message || "Failed to create project permission. Please try again.";
      AntdAlert.error(errorMessage);
    }
    yield put(actions.createUserProjectError());
  }
}

export function* handleUpdateUserProject({ data }) {
  const { updateData } = data;
  try {
    const response = yield axios.put(`${baseURL}/user_projects/${updateData.id}`, {
      ...updateData,
    });
    if (response.status === 200) {
      AntdAlert.success("Project permissions updated successfully");
      yield put(actions.updateUserProjectSuccess(response.data));
      const { userId } = response.data;
      yield put(authActions.fetchSidebarProject({ userId }));
    } else{
      AntdAlert.error(response.data?.errors[0]);
      yield put(actions.updateUserProjectError());
      errorHandler(response);
    }
  } catch ({ response }) {
    yield put(actions.updateUserProjectError(response.data.data));
    AntdAlert.error("Something went wrong!");
    console.log(response);
  }
}


export function* handleDeleteUserProject({ data }) {
  try {
    const response = yield axios.delete(
      `${baseURL}/user_projects/${data.id}`
    );
    if (response.status === 200) {
      AntdAlert.success("Project deleted successfully");
    let resData = { ...response.data };
    yield put(actions.deleteUserProjectSuccess({ ...resData }));
    } else {
      errorHandler(response.data);
      yield put(actions.deleteUserProjectError());
    }
  } catch ({ response }) {
    yield put(actions.deleteUserProjectError());
    AntdAlert.error("Something went wrong!");
    console.log(response);
  }
}

export function* handleFetchAllUserProjects({ data }) {
  try {
    let params = {};

    if (data?.user_id) {
      params = {
        ...params,
        userId: data.user_id,
      };
    }else if (data?.userId) {
      params = {
        ...params,
        userId: data.userId,
      };
    }

    if (data?.search) {
      params = {
        ...params,
        ...data.search,
      };
    }
    
    if (data?.orderBy && data?.orderDirection) {
      params = {
        ...params,
        orderBy: data.orderBy,
        orderDirection: data.orderDirection,
      };
    }
    else if (data?.sortedInfo?.order) {
      params = {
        ...params,
        sort:
          data.sortedInfo.field === "project_id"
            ? "project_number"
            : data.sortedInfo.field,
        order: data?.sortedInfo?.order === "ascend" ? "asc" : "desc",
      };
    }

    const response = yield axios.get(
      `${baseURL}/user_projects`,
      { params }
    );
    
    if (response.status === 200) {
      // The API returns { userProjects: [], total: 0, page: 1, perPage: 10 } when no projects exist
      yield put(actions.fetchAllUserProjectsSuccess({ 
        projects: response.data.userProjects || [],
        total: response.data.total || 0,
        page: response.data.page || 1,
        perPage: response.data.perPage || 10,
        userId: data.userId 
      }));
    } else {
      errorHandler(response.data);
      yield put(actions.fetchAllUserProjectsError());
    }
  } catch (error) {
    console.error('Error fetching user projects:', error);
    errorHandler(error.response?.data || { message: 'Failed to fetch user projects' });
    yield put(actions.fetchAllUserProjectsError());
  }
}
